/**
 * Locale configuration — the one place the locale set is declared.
 *
 * Eight locales ship: English and Thai, which WPML had active and which have
 * their own WooCommerce snapshots, plus Japanese, French, Spanish and Russian,
 * which WPML published and then switched off, and Simplified Chinese and
 * Vietnamese, which WPML never had at all.
 *
 * Reviving those four was not a matter of flipping a flag. By the time the work
 * was done the live site had already retired them — `/fr/a-propos-de-nous` now
 * 301s to `/about-us` on WordPress — so **none of their translated page content
 * was recoverable**. What survived is in this repo: `ja` and `ru` (and `zh`) have
 * all 22 strain bodies translated; `fr` and `es` had nothing at all.
 *
 * **Everything not inherited from WPML is machine-translated and awaits native
 * review.** That includes all UI strings for the four, every page component, the
 * legal text and the category intros. Treat the copy as provisional. The same
 * holds for `zh` and `vi` in full: neither has a WPML inheritance of any kind,
 * so every word of both is machine-translated.
 *
 * ## What differs between the two groups
 *
 * `en` and `th` have their own commerce snapshot under `content/wordpress/`.
 * The other six do not, so `commerceLocaleFor` in `services/products` resolves
 * them to English: product names, prices and category slugs come from the English
 * catalogue while the *body* copy comes from `page-<locale>.svx` where it exists.
 */

export const LOCALES = ['en', 'th', 'ja', 'fr', 'es', 'ru', 'zh', 'vi'] as const;
export type Locale = (typeof LOCALES)[number];

export const DEFAULT_LOCALE: Locale = 'en';

/**
 * Locales with their own WooCommerce snapshot in `content/wordpress/`.
 *
 * Everything else falls back to the English catalogue for names and prices.
 */
export const COMMERCE_LOCALES = ['en', 'th'] as const;
export type CommerceLocale = (typeof COMMERCE_LOCALES)[number];

/**
 * Which catalogue's commerce data a locale reads.
 *
 * Only `en` and `th` have a WooCommerce snapshot, so the other six locales
 * resolve to English for product names, prices and category slugs. Their body
 * copy still comes from `page-<locale>.svx`.
 *
 * Every call into `getProducts`/`getCategories` must go through this — passing a
 * `SiteLocale` straight in is a type error precisely so the fallback cannot be
 * forgotten.
 */
export function commerceLocale(locale: Locale): CommerceLocale {
	return (COMMERCE_LOCALES as readonly string[]).includes(locale)
		? (locale as CommerceLocale)
		: 'en';
}

/**
 * `hreflang` values.
 *
 * Thai targets Thailand specifically because that is the market. The other six
 * are language-only: a Japanese speaker buying seeds shipped within Thailand is
 * not necessarily in Japan, and `ja-JP` would wrongly narrow it. Vietnamese is
 * the same case and for the same reason — the audience is Vietnamese speakers in
 * Thailand at least as much as in Vietnam, so `vi`, not `vi-VN`.
 */
export const HREFLANG: Record<Locale, string> = {
	en: 'en',
	th: 'th-TH',
	ja: 'ja',
	fr: 'fr',
	es: 'es',
	ru: 'ru',
	// Script subtag, not a region: this is Simplified Chinese, which is read in
	// mainland China, Singapore and Malaysia alike. `zh-CN` would wrongly narrow
	// it to one country, and bare `zh` does not say which script.
	zh: 'zh-Hans',
	vi: 'vi'
};

/** `<html lang>` values. */
export const HTML_LANG: Record<Locale, string> = {
	en: 'en',
	th: 'th',
	ja: 'ja',
	fr: 'fr',
	es: 'es',
	ru: 'ru',
	zh: 'zh-Hans',
	vi: 'vi'
};

/** Open Graph locale tags. */
export const OG_LOCALE: Record<Locale, string> = {
	en: 'en_US',
	th: 'th_TH',
	ja: 'ja_JP',
	fr: 'fr_FR',
	es: 'es_ES',
	ru: 'ru_RU',
	// Open Graph predates script subtags and only accepts language_TERRITORY, so
	// Simplified Chinese has to be spelled as a territory here even though
	// `hreflang` above correctly uses the script.
	zh: 'zh_CN',
	vi: 'vi_VN'
};

/**
 * URL prefix for a locale. English is unprefixed, matching the live site.
 */
export function localePrefix(locale: Locale): string {
	return locale === DEFAULT_LOCALE ? '' : `/${locale}`;
}

export function isLocale(value: string): value is Locale {
	return (LOCALES as readonly string[]).includes(value);
}

/**
 * Prefix a path for a locale.
 *
 * **Only valid for routes whose path really is the English one with a prefix** —
 * the fixed routes (`/shop`, `/cart`, `/checkout`, `/sitemap`) and the product
 * and category pages, which use the strain directory and English category slug in
 * every locale.
 *
 * It is *not* valid for the hand-written pages, the legal documents or blog
 * posts: WPML translated those slugs, so `/ja/about-us` is a URL that has never
 * existed while `/ja/会社概要` is the one that has. Resolve those through
 * `pagePath()` and the frontmatter `path` instead. This is the single most
 * repeated source of bugs in this codebase.
 */
export function prefixPath(path: string, locale: Locale): string {
	const prefix = localePrefix(locale);
	if (!prefix) return path;
	return path === '/' ? prefix : `${prefix}${path}`;
}

/**
 * Strip a locale prefix, segment-safely.
 *
 * `/theatre` must not match `/th`, `/espresso` must not match `/es`, and
 * `/vitamins` must not match `/vi` — the first is a real failure mode that
 * already filed an English blog post under Thai.
 */
export function withoutLocalePrefix(pathname: string): string {
	for (const locale of LOCALES) {
		if (locale === DEFAULT_LOCALE) continue;
		if (pathname === `/${locale}`) return '/';
		if (pathname.startsWith(`/${locale}/`)) return pathname.slice(locale.length + 1);
	}
	return pathname;
}

/** The locale a path belongs to, from its prefix. */
export function localeFromPath(pathname: string): Locale {
	for (const locale of LOCALES) {
		if (locale === DEFAULT_LOCALE) continue;
		if (pathname === `/${locale}` || pathname.startsWith(`/${locale}/`)) return locale;
	}
	return DEFAULT_LOCALE;
}
